import { useCallback, useEffect, useRef, useState } from 'react';
import { Panel, usePanelDocked } from './Panel';
import { db } from '@/db/db';
import { useAppState } from '@/ui/AppState';
import { WHITEBOARD_MAX_STROKES, WHITEBOARD_PEN_WIDTHS } from '@/constants/app';
import type { BoardStroke } from '@/domain/types';

/**
 * A whiteboard, for working something out next to the question that asked it.
 *
 * Strokes are stored as fractions of the canvas rather than pixels, so the
 * board redraws correctly at any size — a phone turned sideways, a panel that
 * is a drawer on a wide screen — and so it survives being reopened. A pen is
 * stored by name, not by colour, so the same board reads right in both themes.
 *
 * It can be kept open beside the question rather than over it. Docked, it
 * holds its own strip of the screen — the bottom of a phone, the right of a
 * laptop — the shell gives up that room, and the board stays put while he
 * reads, answers and moves to the next question.
 *
 * Device-local, never synced: scratch paper is not something to merge.
 */

type Pen = BoardStroke['pen'];
type Tool = Pen | 'eraser';

const PEN_COLORS: Record<Pen, { light: string; dark: string }> = {
  ink: { light: '#16202b', dark: '#e8eef5' },
  red: { light: '#c9483f', dark: '#e0837b' },
  blue: { light: '#2e6da0', dark: '#6fb3e0' },
  green: { light: '#2f9e5b', dark: '#5fcb8b' },
};

const PEN_LABELS: Record<Pen, string> = {
  ink: 'Ink',
  red: 'Red',
  blue: 'Blue',
  green: 'Green',
};

/** How close an eraser pass has to come to a stroke to take it out. */
const ERASER_REACH = 0.02;

export function WhiteboardPanel({ onClose }: { onClose: () => void }) {
  const { theme, toast } = useAppState();
  const docked = usePanelDocked();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [strokes, setStrokes] = useState<BoardStroke[]>([]);
  const [tool, setTool] = useState<Tool>('ink');
  const [width, setWidth] = useState<number>(WHITEBOARD_PEN_WIDTHS[1]);
  const [loaded, setLoaded] = useState(false);

  // The stroke being drawn right now, kept in a ref so a pointermove does not
  // re-render the whole panel sixty times a second.
  const live = useRef<BoardStroke | null>(null);
  const drawing = useRef(false);

  // Load the board as it was left.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const saved = await db.boards.get('board').catch(() => undefined);
      if (cancelled) return;
      if (saved) setStrokes(saved.strokes);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Saved after each change rather than on close, because a panel can go away
  // without a close — a reload, a crash, the tab being discarded.
  useEffect(() => {
    if (!loaded) return;
    void db.boards.put({ id: 'board', strokes, updatedAt: Date.now() }).catch(() => {});
  }, [strokes, loaded]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const ratio = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * ratio) || canvas.height !== Math.round(h * ratio)) {
      canvas.width = Math.round(w * ratio);
      canvas.height = Math.round(h * ratio);
    }

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const all = live.current ? [...strokes, live.current] : strokes;
    for (const stroke of all) {
      const points = stroke.points;
      if (points.length < 2) continue;
      ctx.strokeStyle = PEN_COLORS[stroke.pen][theme === 'dark' ? 'dark' : 'light'];
      ctx.lineWidth = stroke.width;
      ctx.beginPath();
      ctx.moveTo(points[0]! * w, points[1]! * h);
      if (points.length === 2) {
        // A tap is a dot: a line to itself draws nothing, so nudge it.
        ctx.lineTo(points[0]! * w + 0.01, points[1]! * h);
      }
      for (let i = 2; i < points.length; i += 2) {
        ctx.lineTo(points[i]! * w, points[i + 1]! * h);
      }
      ctx.stroke();
    }
  }, [strokes, theme]);

  useEffect(redraw, [redraw]);

  // The panel is a sheet on a phone and a drawer on a wide screen, so the
  // canvas changes size without the window doing anything.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => redraw());
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [redraw]);

  function pointAt(event: React.PointerEvent<HTMLCanvasElement>): [number, number] {
    const rect = event.currentTarget.getBoundingClientRect();
    return [
      Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
      Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
    ];
  }

  function eraseAt([x, y]: [number, number]) {
    setStrokes((current) =>
      current.filter((stroke) => {
        for (let i = 0; i < stroke.points.length; i += 2) {
          const dx = stroke.points[i]! - x;
          const dy = stroke.points[i + 1]! - y;
          if (Math.hypot(dx, dy) <= ERASER_REACH) return false;
        }
        return true;
      }),
    );
  }

  function onDown(event: React.PointerEvent<HTMLCanvasElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const point = pointAt(event);
    if (tool === 'eraser') {
      eraseAt(point);
      return;
    }
    live.current = { pen: tool, width, points: [point[0], point[1]] };
    redraw();
  }

  function onMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const point = pointAt(event);
    if (tool === 'eraser') {
      eraseAt(point);
      return;
    }
    if (!live.current) return;
    live.current.points.push(point[0], point[1]);
    redraw();
  }

  function onUp() {
    drawing.current = false;
    const stroke = live.current;
    live.current = null;
    if (!stroke) return;
    setStrokes((current) => {
      const next = [...current, stroke];
      // A board that grows without bound would eventually be slow to redraw
      // and slow to store. The oldest ink is the ink least likely to be wanted.
      return next.length > WHITEBOARD_MAX_STROKES
        ? next.slice(next.length - WHITEBOARD_MAX_STROKES)
        : next;
    });
  }

  return (
    <Panel title="Whiteboard" onClose={onClose}>
      <div className="stack board">
        <div className="board__tools" role="group" aria-label="Pens">
          {(Object.keys(PEN_COLORS) as Pen[]).map((pen) => (
            <button
              key={pen}
              className="board__pen"
              aria-pressed={tool === pen}
              aria-label={PEN_LABELS[pen]}
              title={PEN_LABELS[pen]}
              style={{ background: PEN_COLORS[pen][theme === 'dark' ? 'dark' : 'light'] }}
              onClick={() => setTool(pen)}
            />
          ))}
          <button
            className="btn btn--sm"
            aria-pressed={tool === 'eraser'}
            onClick={() => setTool('eraser')}
          >
            Eraser
          </button>
          <span className="board__widths" role="group" aria-label="Pen width">
            {WHITEBOARD_PEN_WIDTHS.map((w) => (
              <button
                key={w}
                className="btn btn--sm"
                aria-pressed={width === w}
                aria-label={`Width ${w}`}
                onClick={() => setWidth(w)}
              >
                <span className="board__dot" style={{ width: w + 2, height: w + 2 }} />
              </button>
            ))}
          </span>
        </div>

        <div className="board__surface" ref={wrapRef}>
          <canvas
            ref={canvasRef}
            className="board__canvas"
            aria-label="Whiteboard. Drawing is not read back as text."
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onPointerLeave={onUp}
          />
        </div>

        <div className="row">
          <button
            className="btn"
            disabled={strokes.length === 0}
            onClick={() => setStrokes((current) => current.slice(0, -1))}
          >
            Undo
          </button>
          <button
            className="btn"
            disabled={strokes.length === 0}
            onClick={() => {
              const count = strokes.length;
              setStrokes([]);
              toast(`Board cleared — ${count} stroke${count === 1 ? '' : 's'} wiped.`);
            }}
          >
            Clear
          </button>
        </div>

        <p className="faint board__note" style={{ margin: 0 }}>
          {docked ? 'Drag the panel edge to resize it. ' : ''}Stays as you left it, on this device. Nothing here is read, marked or synced — it is
          scratch paper.
        </p>
      </div>
    </Panel>
  );
}
